import React from 'react'
// libs
import DOMPurify from 'dompurify'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useApolloClient } from 'hooks/useApolloClient'
// constants
import { CURRENCIES } from 'constants/currencies'
// context
import { ActivitiesListBLContext } from 'containers/Activities/layers/BL/ActivitiesList'
import { ActivityBLContext } from 'containers/Activities/layers/BL/Activity'
import { ResourcesListAPIContext } from 'containers/Activities/layers/API/ResourcesList'
import { ResourcesListBLContext } from 'containers/Activities/layers/BL/ResourcesList'
// view components
import ResourceActionsMenu from './components/ActionsMenu'
// views
import WorkRounded from '@mui/icons-material/WorkRounded'
import { CreatedAt, MetaContent } from './components'
import { Content, MetadataWrapper, Paper, ResourceContent, ResourceHeader, ResourceWrapper } from './views'
// themes
import { TextBold } from 'themes/views/common'
// types
import { FieldType, IField, IFieldValueMapItem, IPlanObjectiveResource, LogEntityTypes } from 'types/graphql'
// utils
import find from 'lodash/fp/find'
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import { safeGetOr } from 'utils/fp'
import { getTableHeader } from 'utils/tables'
import { getFieldsInitialValues } from 'utils/form'

const numberFormatter = new Intl.NumberFormat('en-AU')

interface IIncomingProps {
  fields: IField[]
  isActivitiesList?: boolean
  resource?: IPlanObjectiveResource
}

const Resource: React.FunctionComponent<IIncomingProps> = ({ fields, isActivitiesList, resource: planResource }) => {
  const { client } = useApolloClient()

  const {
    company: { currency: companyCurrency }
  } = useCurrentUser()

  const [initialValues, setInitialValues] = React.useState({})

  const { handleEditClick } = React.useContext(ActivitiesListBLContext)

  const { activity } = React.useContext(ActivityBLContext)
  const { canModify, canDelete } = React.useContext(ResourcesListAPIContext)
  const { handleEditResource } = React.useContext(ResourcesListBLContext)

  let resource = get(activity, 'metadata.resource') || planResource

  const createdBy = get(activity, 'user') || get(resource, 'createdBy')

  const fieldValueMap = resource.fieldValueMap || [
    {
      fieldName: 'criticality',
      fieldValue: { value: get(resource, 'criticality.value') },
      fieldType: FieldType.PickList
    },
    {
      fieldName: 'resourceCategory',
      fieldValue: { value: get(resource, 'resourceCategory.value') },
      fieldType: FieldType.PickList
    }
  ]

  const getInitialValues = async () =>
    await getFieldsInitialValues({
      fieldValues: fieldValueMap,
      fields,
      client
    })

  const editResource = () => {
    if (isActivitiesList) {
      return handleEditClick({ entityType: LogEntityTypes.Resource, metadata: { resource: { id: resource.id } } })
    }
    handleEditResource(resource)
  }

  React.useEffect(() => {
    if (resource) {
      getInitialValues().then(setInitialValues)
    }
    setInitialValues({})
  }, [resource.id, resource.fieldValueMap])

  if (planResource) {
    const resourceDescription = pipe(
      find((field: IFieldValueMapItem) => field.fieldName === 'resourceDescription'),
      safeGetOr<any, any, any>('N/A', 'fieldValue')
    )(resource.fieldValueMap)

    resource = { ...resource, ...initialValues, resourceDescription }
  } else {
    resource = { ...resource, ...initialValues }
  }

  return (
    <Paper>
      {!isActivitiesList && (canModify || canDelete) && <ResourceActionsMenu resource={resource} />}

      <ResourceWrapper>
        <WorkRounded />
        <ResourceContent>
          <ResourceHeader>
            <TextBold onClick={editResource}>{get(resource, 'resourceCategory.label') || 'N/A'}</TextBold>
          </ResourceHeader>
          <Content
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(resource.resourceDescription ?? '')
            }}
          />
          <MetadataWrapper>
            <MetaContent label={getTableHeader('resourceBudget', fields, 'Budget')}>
              {resource.resourceBudget
                ? `${CURRENCIES[companyCurrency ?? 'USD'].symbol} ${numberFormatter.format(resource.resourceBudget)}`
                : 'N/A'}
            </MetaContent>
            <MetaContent label={getTableHeader('resourceCost', fields, 'Cost')}>
              {resource.resourceCost
                ? `${CURRENCIES[companyCurrency ?? 'USD'].symbol} ${numberFormatter.format(resource.resourceCost)}`
                : 'N/A'}
            </MetaContent>
            <MetaContent label={getTableHeader('criticality', fields, 'Criticality')}>
              {get(resource, 'criticality.label') || 'N/A'}
            </MetaContent>
            <MetaContent label={'CREATED BY'}>
              {createdBy.firstName} {createdBy.lastName}
            </MetaContent>
            <CreatedAt date={resource.createdAt} />
          </MetadataWrapper>
        </ResourceContent>
      </ResourceWrapper>
    </Paper>
  )
}

export default Resource
